/**
 * The bounded session activity that can travel with a session header.
 *
 * This deliberately lives beside the log writers: a compact surface must never
 * discover a reply by projecting a session's entire historical transcript.
 */

import { decodeEntry } from './codec'
import type { MessageData } from './types'

export const MAX_STRIP_REPLY_CHARS = 8_000
export const MAX_STRIP_QUESTION_CHARS = 32_000
export const MAX_STRIP_ACTIVITY_CHARS = 500

export type SessionStripActivity =
  | { kind: 'reply'; text: string; truncated?: boolean }
  | { kind: 'question'; interactionId: string; bodyJson: string }
  | { kind: 'text'; text: string; truncated?: boolean }
  | { kind: 'tool'; name: string; summary?: string }

const IDENTIFYING_ARGS = [
  'command',
  'file_path',
  'filePath',
  'path',
  'pattern',
  'query',
  'url',
  'description',
  'title',
  'name',
  'prompt',
  'text',
] as const

function scalarText(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  const scalar = (value as { val?: unknown } | null)?.val
  return typeof scalar === 'string' ? scalar : undefined
}

function boundedLine(
  value: string,
  latest = false,
): { text: string; truncated?: boolean } {
  const lines = value
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
  const text = (latest ? lines.at(-1) : lines[0]) ?? ''
  if (text.length <= MAX_STRIP_ACTIVITY_CHARS) return { text }
  return {
    text: `${text.slice(0, MAX_STRIP_ACTIVITY_CHARS - 1)}…`,
    truncated: true,
  }
}

function toolSummary(argumentsValue: unknown): string | undefined {
  const direct = scalarText(argumentsValue)
  if (direct?.trim()) return boundedLine(direct).text
  if (!argumentsValue || typeof argumentsValue !== 'object') return undefined
  const record = argumentsValue as Record<string, unknown>
  for (const key of IDENTIFYING_ARGS) {
    const value = scalarText(record[key])
    if (value?.trim()) return boundedLine(value).text
  }
  for (const candidate of Object.values(record)) {
    const value = scalarText(candidate)
    if (value?.trim()) return boundedLine(value).text
  }
  return undefined
}

function bareToolName(name: string): string {
  return name.replace(/^mcp__[^_]+(?:_[^_]+)*?__/, '').replace(/^mod[._]/, '')
}

/** A completed or streaming assistant message is the compact reply card. */
export function stripReplyFromMessage(
  message: MessageData,
): SessionStripActivity | null {
  if (
    message.role !== 'assistant' ||
    (message.status !== 'completed' && message.status !== 'streaming')
  )
    return null
  const text = message.text.trim()
  if (!text) return null
  if (text.length <= MAX_STRIP_REPLY_CHARS) return { kind: 'reply', text }
  return {
    kind: 'reply',
    text: `${text.slice(0, MAX_STRIP_REPLY_CHARS - 1)}…`,
    truncated: true,
  }
}

/** The latest bounded step of a live assistant message. */
export function stripLiveActivityFromMessage(
  message: MessageData,
): SessionStripActivity | null {
  if (message.role !== 'assistant') return null
  const tool = message.toolUses.at(-1)
  if (tool) {
    const summary = toolSummary(tool.arguments)
    return {
      kind: 'tool',
      name: bareToolName(tool.name),
      ...(summary ? { summary } : {}),
    }
  }
  const line = boundedLine(message.text, true)
  return line.text ? { kind: 'text', ...line } : null
}

/**
 * Read ONE current assistant entry, never a projected transcript. The session
 * header carries its id while the response streams, so this stays O(1) no
 * matter how much history the session has accumulated.
 */
export function stripReplyFromEntry(raw: unknown): SessionStripActivity | null {
  if (!raw || typeof raw !== 'object') return null
  try {
    const entry = decodeEntry(raw as Parameters<typeof decodeEntry>[0])
    if (entry.payload.type !== 'message') return null
    return stripReplyFromMessage(entry.payload.payload)
  } catch {
    return null
  }
}

/** Read one current assistant entry as live activity, without a transcript fold. */
export function stripLiveActivityFromEntry(
  raw: unknown,
): SessionStripActivity | null {
  if (!raw || typeof raw !== 'object') return null
  try {
    const entry = decodeEntry(raw as Parameters<typeof decodeEntry>[0])
    if (entry.payload.type !== 'message') return null
    return stripLiveActivityFromMessage(entry.payload.payload)
  } catch {
    return null
  }
}

/** Accept only the bounded shapes a session header may carry. */
export function readStripActivity(value: unknown): SessionStripActivity | null {
  if (!value || typeof value !== 'object') return null
  const raw = value as Record<string, unknown>
  if (String(raw.kind) === 'reply' && raw.text != null) {
    const text = String(raw.text)
    if (!text || text.length > MAX_STRIP_REPLY_CHARS) return null
    return {
      kind: 'reply',
      text,
      ...(raw.truncated === true ? { truncated: true } : {}),
    }
  }
  if (String(raw.kind) === 'text' && raw.text != null) {
    const text = String(raw.text)
    if (!text || text.length > MAX_STRIP_ACTIVITY_CHARS) return null
    return {
      kind: 'text',
      text,
      ...(raw.truncated === true ? { truncated: true } : {}),
    }
  }
  if (String(raw.kind) === 'tool' && raw.name != null) {
    const name = String(raw.name)
    const summary = raw.summary == null ? '' : String(raw.summary)
    if (
      !name ||
      name.length > MAX_STRIP_ACTIVITY_CHARS ||
      summary.length > MAX_STRIP_ACTIVITY_CHARS
    )
      return null
    return { kind: 'tool', name, ...(summary ? { summary } : {}) }
  }
  if (
    String(raw.kind) === 'question' &&
    raw.interactionId != null &&
    raw.bodyJson != null
  ) {
    const bodyJson = String(raw.bodyJson)
    if (!bodyJson || bodyJson.length > MAX_STRIP_QUESTION_CHARS) return null
    return {
      kind: 'question',
      interactionId: String(raw.interactionId),
      bodyJson,
    }
  }
  return null
}
